import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
    bp_assistant_ask,
    bp_ai_review_posts,
} from '../../../services';
import { unwrap, mdNormalize, timeAgo, NOVA_LOGO_SRC } from './_shared';
import { Icon } from './_icons';
import ModuleIcon from '../../../Components/ModuleIcon';
import { platformLabel } from './LanguageAnalysis';
import CommonActionMenu from '../../../Components/CommonActionMenu';
import CommonButton from '../../../Components/CommonButton';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonLoader from '../../../Components/CommonLoader';
import CommonPagination from '../../../Components/CommonPagination';
import CommonTabs from '../../../Components/CommonTabs';
import NoDataPage from '../../../Components/NoDataPage';
import StatCard from '../../../Components/StatCard';
import StatusBadge from '../../../Components/StatusBadge';
import AddOnCard from '../../AddOns/AddOnCard';
import { lightTheme as T } from '../../../Constants/Colors';
import { Heading, SubHeading, Body } from '../../../Constants/Fonts';
import './brand-mentions.scss';

const LS_KEY = 'bm_asst_chats_v1';

const REVIEW_DAYS_OPTIONS = [
    { value: 7, label: 'Last 7 days' },
    { value: 15, label: 'Last 15 days' },
    { value: 30, label: 'Last 30 days' },
];

const SEV = {
    high:   { variant: 'danger',  label: 'High' },
    medium: { variant: 'warning', label: 'Monitor' },
    low:    { variant: 'neutral', label: 'Low' },
};

const STROKE = 'currentColor';

const ICON_SUMMARY = (
    <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
        <rect x="2.5" y="2.5" width="11" height="11" rx="1.5"
              fill="none" stroke={STROKE} strokeWidth="1.2" />
        <line x1="5" y1="6" x2="11" y2="6" stroke={STROKE} strokeWidth="1.2" />
        <line x1="5" y1="8.5" x2="11" y2="8.5" stroke={STROKE} strokeWidth="1.2" />
        <line x1="5" y1="11" x2="9" y2="11" stroke={STROKE} strokeWidth="1.2" />
    </svg>
);

const ICON_COMPETITOR = (
    <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
        <rect x="2"  y="9" width="4" height="5" fill={STROKE} opacity="0.55" />
        <rect x="10" y="5" width="4" height="9" fill={STROKE} />
    </svg>
);

const ICON_GROWTH = (
    <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
        <polyline points="2,12 6,8 9,10 14,4"
                  fill="none" stroke={STROKE} strokeWidth="1.4"
                  strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="11,4 14,4 14,7"
                  fill="none" stroke={STROKE} strokeWidth="1.4"
                  strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ICON_SPIKE = (
    <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
        <polyline points="1,12 4,11 6,6 8,13 10,9 13,10 15,8"
                  fill="none" stroke={STROKE} strokeWidth="1.3"
                  strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ICON_LEADS = (
    <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="8" r="6"   fill="none" stroke={STROKE} strokeWidth="1.2" />
        <circle cx="8" cy="8" r="3.2" fill="none" stroke={STROKE} strokeWidth="1.2" />
        <circle cx="8" cy="8" r="1.2" fill={STROKE} />
    </svg>
);

const QUICK_STARTS = [
    { key: 'summary',     icon: ICON_SUMMARY,    label: 'Project Summary',
      desc: 'Volume, sentiment & top narratives at a glance',
      prompt: 'Give me a project summary of brand mentions in the last 14 days.' },
    { key: 'competitor',  icon: ICON_COMPETITOR, label: 'Competitor Impact',
      desc: 'Benchmark mention volume vs the prior period',
      prompt: 'How does my brand mention volume compare to the prior period?' },
    { key: 'growth',      icon: ICON_GROWTH,     label: 'Brand Growth Strategy',
      desc: 'Recommendations from recent topic trends',
      prompt: 'Suggest a brand growth strategy based on recent topic trends.' },
    { key: 'spikes',      icon: ICON_SPIKE,      label: 'Engagement Spikes',
      desc: 'Find engagement peaks and what drove them',
      prompt: 'When did engagement spike, and what topics drove it?' },
    { key: 'leads',       icon: ICON_LEADS,      label: 'Lead Opportunities',
      desc: 'Positive-sentiment posts worth acting on',
      prompt: 'What positive-sentiment posts look like lead opportunities?' },
];

function uid() {
    return 'c_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6);
}

// A chat's own `title` wins once the user renames it; otherwise fall back to
// the first thing they asked.
function chatTitle(thread, title) {
    if (title && title.trim()) return title.trim();
    const first = (thread || []).find(m => m.role === 'user');
    if (!first) return 'New chat';
    return first.content.slice(0, 50) + (first.content.length > 50 ? '…' : '');
}

function fmtDate(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString(undefined,
            { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (_) { return iso; }
}

const MODES = [
    { key: 'chat',    label: 'Chat' },
    { key: 'review',  label: 'FORESEE AI Review' },
];

function ModeTabs({ mode, setMode }) {
    return (
        <CommonTabs
            tabs={MODES.map(m => m.label)}
            values={MODES.map(m => m.key)}
            active={mode}
            handlers={MODES.map(m => () => setMode(m.key))}
        />
    );
}

function Toolbar({ tabs, children }) {
    return (
        <div className="bm_as_toolbar">
            {tabs}
            {children && <div className="bm_as_toolbar_controls">{children}</div>}
        </div>
    );
}

function Panel({ title, note, className = '', children }) {
    return (
        <section className={`bm_as_panel cmn_elevated_card ${className}`.trim()}>
            {(title || note) && (
                <div className="bm_as_panel_head">
                    {title && <SubHeading as="h3" variant={2} className="bm_as_panel_title">{title}</SubHeading>}
                    {note && <Body as="span" variant={2} className="bm_as_muted">{note}</Body>}
                </div>
            )}
            {children}
        </section>
    );
}

// Matches src/NmtAI/Nmtai.js - the dashboard chat labels the user bubble with
// the first letter of the signed-in email rather than the word "You".
const userInitial = () => {
    const email = localStorage.getItem('email');
    return email ? email.charAt(0).toUpperCase() : 'U';
};

const NovaAvatar = () => (
    <img className="bm_as_avatar is-nova" src={NOVA_LOGO_SRC} alt="" width={32} height={32} />
);

function CopyButton({ text }) {
    // `done` is the transient confirmation; `copiedOnce` persists for the life
    // of the message, so a second copy of the same answer says so rather than
    // repeating "Copied" as if it were new.
    const [done, setDone] = useState(false);
    const [copiedOnce, setCopiedOnce] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const copy = () => {
        try {
            navigator.clipboard.writeText(text || '');
            setDone(true);
            setCopiedOnce(true);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setDone(false), 1600);
        } catch (_) { /* ignore */ }
    };

    // Copy -> (click) Copied -> (settles) Already copied -> (click) Copied ...
    const label = done ? 'Copied' : (copiedOnce ? 'Already copied' : 'Copy');

    return (
        <Body as="button" type="button" variant={2} weight="semibold" className="bm_as_link"
            onClick={copy} title={copiedOnce ? 'Copy this answer again' : 'Copy answer'}>
            {label}
        </Body>
    );
}

const TypingDots = () => (
    <span className="bm_as_typing" aria-label="FORESEE AI is thinking">
        <span /><span /><span />
    </span>
);

function ChatMessage({ msg }) {
    const isUser = msg.role === 'user';
    return (
        <div className={`bm_as_msg ${isUser ? 'is-user' : 'is-nova'}`}>
            {isUser
                ? <span className="bm_as_avatar is-user" aria-hidden="true">{userInitial()}</span>
                : <NovaAvatar />}
            <div className="bm_as_bubble">
                <Body as="div" variant={2} weight="bold" className="bm_as_msg_name">{isUser ? 'You' : 'FORESEE AI'}</Body>
                {isUser
                    ? <Body className="bm_as_msg_text">{msg.content}</Body>
                    : <div className="bm_md"><ReactMarkdown>{mdNormalize(msg.content)}</ReactMarkdown></div>}
                {!isUser && !msg.error && (
                    <div className="bm_as_msg_actions"><CopyButton text={msg.content} /></div>
                )}
            </div>
        </div>
    );
}

function ChatMode({ tabs }) {
    const [chats, setChats]       = useState([]);  // [{id, thread, updatedAt}]
    const [activeId, setActiveId] = useState(null);
    const [input, setInput]       = useState('');
    const [busy, setBusy]         = useState(false);
    const endRef = useRef(null);
    const taRef  = useRef(null);
    const [renamingId, setRenamingId]   = useState(null);
    const [renameDraft, setRenameDraft] = useState('');

    useEffect(() => {
        try {
            const raw = localStorage.getItem(LS_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length) {
                    setChats(parsed);
                    setActiveId(parsed[0].id);
                    return;
                }
            }
        } catch (_) { /* ignore */ }
        const fresh = { id: uid(), thread: [], updatedAt: Date.now() };
        setChats([fresh]);
        setActiveId(fresh.id);
    }, []);

    useEffect(() => {
        if (chats.length === 0) return;
        try { localStorage.setItem(LS_KEY, JSON.stringify(chats)); } catch (_) {}
    }, [chats]);

    const activeChat = chats.find(c => c.id === activeId) || { thread: [] };
    const thread = activeChat.thread || [];

    const updateActive = (mutator) => {
        setChats(prev => prev.map(c =>
            c.id === activeId ? { ...c, thread: mutator(c.thread || []), updatedAt: Date.now() } : c
        ));
    };

    const newChat = () => {
        const fresh = { id: uid(), thread: [], updatedAt: Date.now() };
        setChats(prev => [fresh, ...prev]);
        setActiveId(fresh.id);
        setInput('');
    };

    const startRename = (chat) => {
        setRenamingId(chat.id);
        setRenameDraft(chatTitle(chat.thread, chat.title));
    };

    const commitRename = (id) => {
        const next = renameDraft.trim();
        setChats(prev => prev.map(c => (c.id === id ? { ...c, title: next || undefined } : c)));
        setRenamingId(null);
        setRenameDraft('');
    };

    const deleteChat = (id) => {
        setChats(prev => {
            const next = prev.filter(c => c.id !== id);
            if (next.length === 0) {
                const fresh = { id: uid(), thread: [], updatedAt: Date.now() };
                setActiveId(fresh.id);
                return [fresh];
            }
            if (id === activeId) setActiveId(next[0].id);
            return next;
        });
    };

    const send = async (text) => {
        if (!text?.trim() || busy) return;
        const userMsg = { role: 'user', content: text.trim() };
        const priorTurns = thread.map(m => ({ role: m.role, content: m.content }));
        updateActive(t => [...t, userMsg]);
        setInput('');
        setBusy(true);
        try {
            const res = await bp_assistant_ask({ question: text.trim(), thread: priorTurns });
            const data = unwrap(res) || {};
            updateActive(t => [...t, {
                role: 'assistant',
                content: data.answer || 'No answer generated.',
            }]);
        } catch (e) {
            updateActive(t => [...t, {
                role: 'assistant',
                content: 'The assistant is temporarily unavailable. Please try again.',
                error: true,
            }]);
        } finally {
            setBusy(false);
        }
    };

    const onSubmit = (e) => { e.preventDefault(); send(input); };
    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
    };
    const grow = (el) => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 140) + 'px';
    };

    // Auto-scroll to the newest message / typing indicator.
    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [thread.length, busy]);

    return (
        <>
            <Toolbar tabs={tabs}>
                <CommonButton text="New chat" variant="primary" onClick={newChat} />
            </Toolbar>

            <div className="bm_as_chat_layout">
                <Panel title="Chats" note={String(chats.length)} className="bm_as_history">
                    <div className="bm_as_history_list">
                        {chats.map(c => {
                            const isActive = c.id === activeId;
                            return (
                                <div key={c.id}
                                    className={`bm_as_history_item${isActive ? ' is-active' : ''}`}
                                    role="button" tabIndex={0} aria-current={isActive || undefined}
                                    onClick={() => setActiveId(c.id)}
                                    onKeyDown={e => { if (e.key === 'Enter') setActiveId(c.id); }}>
                                    <div className="bm_as_history_text">
                                        {renamingId === c.id ? (
                                            <input
                                                className="bm_as_rename"
                                                aria-label="Chat name"
                                                value={renameDraft}
                                                autoFocus
                                                onClick={ev => ev.stopPropagation()}
                                                onChange={ev => setRenameDraft(ev.target.value)}
                                                onBlur={() => commitRename(c.id)}
                                                onKeyDown={ev => {
                                                    ev.stopPropagation();
                                                    if (ev.key === 'Enter') commitRename(c.id);
                                                    if (ev.key === 'Escape') { setRenamingId(null); setRenameDraft(''); }
                                                }}
                                            />
                                        ) : (
                                            <Body as="span" variant={2} weight={isActive ? 'semibold' : 'medium'}
                                                className="bm_as_history_title">
                                                {chatTitle(c.thread, c.title)}
                                            </Body>
                                        )}
                                        <Body as="span" variant={2} className="bm_as_muted">
                                            {c.updatedAt ? timeAgo(new Date(c.updatedAt).toISOString()) : ''}
                                        </Body>
                                    </div>
                                    <span className="bm_as_history_menu" onClick={ev => ev.stopPropagation()}>
                                        <CommonActionMenu
                                            inlineThreshold={1}
                                            minWidth={140}
                                            ariaLabel="Chat options"
                                            actions={[
                                                { label: 'Rename', onClick: () => startRename(c) },
                                                { label: 'Delete', danger: true, onClick: () => deleteChat(c.id) },
                                            ]}
                                        />
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </Panel>

                <section className="bm_as_chat cmn_elevated_card">
                    {thread.length === 0 ? (
                        <div className="bm_as_hero">
                            <img className="bm_as_hero_logo" src={NOVA_LOGO_SRC} alt="" width={56} height={56} />
                            <Body as="div" variant={2} weight="bold" className="bm_as_kicker">FORESEE AI Assistant</Body>
                            <Heading as="h3" variant={2} className="bm_as_hero_title">Hello, how can I help you?</Heading>
                            <Body className="bm_as_hero_text">
                                Ask about your brand's mentions, sentiment, narratives, sources and
                                influencers from the last 14 days, or start with a suggestion below.
                            </Body>
                            <div className="bm_as_starters">
                                {QUICK_STARTS.map(q => (
                                    <AddOnCard key={q.key}
                                        align="flex-start"
                                        iconNode={<span className="bm_as_starter_icon">{q.icon}</span>}
                                        label={(
                                            <>
                                                <Body as="div" weight="semibold" className="bm_as_card_title">{q.label}</Body>
                                                <Body as="div" variant={2} className="bm_as_card_text">{q.desc}</Body>
                                            </>
                                        )}
                                        action={null}
                                        onClick={busy ? undefined : () => send(q.prompt)}
                                        title={q.prompt}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="bm_as_msgs">
                            {thread.map((msg, i) => <ChatMessage key={i} msg={msg} />)}
                            {busy && (
                                <div className="bm_as_msg is-nova">
                                    <NovaAvatar />
                                    <div className="bm_as_bubble">
                                        <Body as="div" variant={2} weight="bold" className="bm_as_msg_name">FORESEE AI</Body>
                                        <TypingDots />
                                    </div>
                                </div>
                            )}
                            <div ref={endRef} />
                        </div>
                    )}

                    <form onSubmit={onSubmit} className="bm_as_composer">
                        <div className="bm_as_composer_box">
                            <textarea
                                ref={taRef}
                                rows={1}
                                aria-label="Message FORESEE AI"
                                placeholder="Ask me any brand-related question…"
                                value={input}
                                onChange={e => { setInput(e.target.value); grow(taRef.current); }}
                                onKeyDown={onKeyDown}
                                disabled={busy} />
                            <CommonButton type="submit" text="Send" variant="primary"
                                disable={busy || !input.trim()} title="Send (Enter)" />
                        </div>
                        <Body as="div" variant={2} className="bm_as_muted bm_as_composer_hint">
                            Enter to send · Shift+Enter for a new line · grounded in your last 14 days of
                            scan data. FORESEE AI can make mistakes.
                        </Body>
                    </form>
                </section>
            </div>
        </>
    );
}

// ===========================================================================
// REVIEW MODE - FORESEE AI scans the last N days and flags suspicious posts.
// ===========================================================================
function SevBadge({ severity }) {
    const s = SEV[severity] || SEV.low;
    return <StatusBadge variant={s.variant} dot={false} transform="uppercase">{s.label}</StatusBadge>;
}

function ScoreMeter({ label, value, sev }) {
    const pct = Math.max(0, Math.min(100, Number(value) || 0));
    return (
        <span className="bm_as_meter" title={`${label} ${pct}`}>
            <Body as="span" variant={2} weight="bold" className="bm_as_label">{label}</Body>
            <span className="bm_as_meter_track">
                <span className={`bm_as_meter_fill is-${sev || 'low'}`} style={{ width: `${pct}%` }} />
            </span>
            <Body as="span" variant={2} weight="semibold" className="bm_as_meter_val">{pct}</Body>
        </span>
    );
}

function ReviewCard({ f }) {
    const sev = f.severity || 'low';
    return (
        <article className="bm_as_flag">
            <div className="bm_as_flag_head">
                <SevBadge severity={sev} />
                <Body as="span" weight="semibold" className="bm_as_flag_handle">@{f.handle}</Body>
                <Body as="span" variant={2} className="bm_as_muted">
                    {platformLabel(f.platform)}{f.posted_at ? ` · ${fmtDate(f.posted_at)}` : ''}
                </Body>
                <span className="bm_as_flag_scores">
                    <ScoreMeter label="Risk" value={f.risk_score} sev={sev} />
                    <ScoreMeter label="Reach" value={f.reach_score} sev={sev} />
                </span>
            </div>

            <Body variant={2} className="bm_as_flag_text">
                {f.snippet}{(f.snippet || '').length >= 280 ? '…' : ''}
            </Body>

            {(f.reasons || []).length > 0 && (
                <div className="bm_as_chips">
                    {f.reasons.map((r, i) => (
                        <StatusBadge key={i} variant="neutral" dot={false} transform="none">{r}</StatusBadge>
                    ))}
                </div>
            )}

            <div className="bm_as_flag_foot">
                {f.suggested_action && (
                    <Body as="span" variant={2} className="bm_as_flag_action">
                        <Body as="span" variant={2} weight="bold" className="bm_as_label">Suggested action</Body>
                        {' '}{f.suggested_action}
                    </Body>
                )}
                {f.url && (
                    <Body as="a" variant={2} weight="semibold" className="bm_as_link"
                        href={f.url} target="_blank" rel="noopener noreferrer">
                        View source ↗
                    </Body>
                )}
            </div>
        </article>
    );
}

const REVIEW_PAGE_SIZE = 10;

function ReviewMode({ tabs }) {
    const [days, setDays]   = useState(15);
    const [busy, setBusy]   = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [page, setPage]   = useState(0);

    const run = async () => {
        setBusy(true); setError(null);
        try {
            const res = await bp_ai_review_posts({ days });
            setResult(unwrap(res) || {});
            setPage(0);
        } catch (e) {
            setError('Review failed. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    const bySev = result?.by_severity || {};
    const flagged = result?.flagged || [];
    const reviewed = Number(result?.total_reviewed) || 0;
    const pageCount = Math.max(1, Math.ceil(flagged.length / REVIEW_PAGE_SIZE));
    const pageItems = flagged.slice(page * REVIEW_PAGE_SIZE, (page + 1) * REVIEW_PAGE_SIZE);
    const rangeLabel = REVIEW_DAYS_OPTIONS.find(o => o.value === days)?.label || '';
    const rangeText = rangeLabel.toLowerCase();
    const high = Number(bySev.high) || 0;
    const medium = Number(bySev.medium) || 0;
    const low = Number(bySev.low) || 0;

    return (
        <>
            <Toolbar tabs={tabs}>
                <CommonDropdown
                    ariaLabel="Date range"
                    value={days}
                    onChange={setDays}
                    options={REVIEW_DAYS_OPTIONS}
                    disabled={busy}
                />
                <CommonButton
                    text={busy ? 'Reviewing…' : 'Run review'}
                    variant="primary"
                    onClick={run} disable={busy}
                />
            </Toolbar>

            {busy && (
                <Panel>
                    <div className="bm_as_loading" aria-live="polite">
                        <CommonLoader label={`FORESEE AI is reading your mentions from the ${rangeText}…`} />
                    </div>
                </Panel>
            )}

            {error && !busy && (
                <Panel>
                    <NoDataPage
                        icon={<Icon name="alert" size={24} color={T.danger} />}
                        title="Review failed"
                        hint={error}
                        action={<CommonButton text="Try again" variant="primary" onClick={run} />}
                    />
                </Panel>
            )}

            {!result && !busy && !error && (
                <Panel>
                    <NoDataPage
                        icon={<Icon name="shield" size={24} color={T.primary} />}
                        title="Ready when you are"
                        hint="Pick a window and press Run review. FORESEE AI weighs sentiment, author risk and amplification, and tells you why each post was flagged."
                    />
                </Panel>
            )}

            {result && !busy && !error && (
                <>
                    {result.headline && (
                        <div className="bm_ai_panel cmn_elevated_card">
                            <div className="bm_ai_panel_head">
                                <img className="bm_ai_panel_logo" src={NOVA_LOGO_SRC} alt="" width={32} height={32} />
                                <div className="bm_ai_panel_titles">
                                    <SubHeading as="h3" variant={3} className="bm_ai_panel_title">FORESEE AI summary</SubHeading>
                                    <Body as="span" variant={2} className="bm_ai_panel_sub">
                                        {rangeLabel} · {reviewed} mention{reviewed === 1 ? '' : 's'} reviewed
                                    </Body>
                                </div>
                            </div>
                            <div className="bm_ai_panel_body">
                                <Body className="bm_as_summary_text">{result.headline}</Body>
                            </div>
                        </div>
                    )}

                    <div className="bm_as_kpis">
                        <StatCard className="cmn_elevated_card"
                            label="Reviewed" value={reviewed} hint={rangeText}
                            icon={<span className="bm_kpi_icon"><ModuleIcon name="compliance" size={18} color="currentColor" /></span>} iconLeft />
                        <StatCard className="cmn_elevated_card"
                            label="High" value={high}
                            valueColor={high > 0 ? T.danger : undefined}
                            hint={high > 0 ? 'act on these first' : 'none'}
                            icon={<span className={`bm_kpi_icon is-${high > 0 ? 'danger' : 'neutral'}`}><ModuleIcon name="risk" size={18} color="currentColor" /></span>} iconLeft />
                        <StatCard className="cmn_elevated_card"
                            label="Monitor" value={medium}
                            valueColor={medium > 0 ? T.warning : undefined}
                            hint={medium > 0 ? 'keep an eye on these' : 'none'}
                            icon={<span className={`bm_kpi_icon is-${medium > 0 ? 'warning' : 'neutral'}`}><ModuleIcon name="history" size={18} color="currentColor" /></span>} iconLeft />
                        <StatCard className="cmn_elevated_card"
                            label="Low" value={low}
                            hint={low > 0 ? 'no action needed yet' : 'none'}
                            icon={<span className="bm_kpi_icon is-neutral"><ModuleIcon name="check_circle" size={18} color="currentColor" /></span>} iconLeft />
                    </div>

                    {flagged.length === 0 ? (
                        <Panel>
                            <NoDataPage
                                icon={<Icon name="check" size={24} color={T.success} />}
                                title="Nothing suspicious in this window"
                                hint={reviewed > 0
                                    ? `FORESEE AI reviewed ${reviewed} mentions from the ${rangeText} and found nothing worth escalating.`
                                    : `No mentions were captured in the ${rangeText}. Widen the window, or check your tracked keywords in Settings.`}
                            />
                        </Panel>
                    ) : (
                        <Panel
                            title="Flagged mentions"
                            note={pageCount > 1
                                ? `${page * REVIEW_PAGE_SIZE + 1}-${Math.min((page + 1) * REVIEW_PAGE_SIZE, flagged.length)} of ${flagged.length}`
                                : `${flagged.length} total`}
                        >
                            <div className="bm_as_flags">
                                {pageItems.map(f => <ReviewCard key={f.id} f={f} />)}
                            </div>
                            {pageCount > 1 && (
                                <div className="bm_comments_pagination">
                                    <CommonPagination
                                        handlePageClick={({ selected }) => setPage(selected)}
                                        forcePage={page}
                                        pageCount={pageCount}
                                    />
                                </div>
                            )}
                        </Panel>
                    )}
                </>
            )}
        </>
    );
}

// ===========================================================================
export default function AssistantTab() {
    const [mode, setMode] = useState('chat');
    const tabs = <ModeTabs mode={mode} setMode={setMode} />;
    return (
        <div className="bm_as">
            {mode === 'chat'   && <ChatMode tabs={tabs} />}
            {mode === 'review' && <ReviewMode tabs={tabs} />}
        </div>
    );
}
